import React, { useState, useEffect } from 'react';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line
} from 'recharts';
import { analyticsService } from '../services/analyticsService';
import './Analytics.css';

const MONTH_SHORT = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

function formatMoney(n) {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
}

/** Дата задачи = дата в календаре (due_date), если нет — report_display_date или created_at. */
function formatTaskDate(task) {
  if (!task) return '—';
  const fromDue = task.due_date ? (typeof task.due_date === 'string' ? task.due_date.slice(0, 10) : new Date(task.due_date).toISOString().slice(0, 10)) : null;
  const fromCreated = task.created_at ? (typeof task.created_at === 'string' ? task.created_at.slice(0, 10) : new Date(task.created_at).toISOString().slice(0, 10)) : null;
  const raw = task.report_display_date || fromDue || fromCreated;
  if (raw == null || raw === '') return '—';
  const str = typeof raw === 'string' ? raw.trim().slice(0, 10) : null;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!str || !dateRegex.test(str)) return '—';
  const d = new Date(str + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const Analytics = () => {
  const [dashboard, setDashboard] = useState(null);
  const [financeChart, setFinanceChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, fRes] = await Promise.all([
        analyticsService.getDashboard(),
        analyticsService.getFinanceChart(period)
      ]);
      setDashboard(dRes.data || null);
      setFinanceChart(fRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading) {
    return (
      <div className="analytics-new">
        <div className="analytics-new__loader" />
      </div>
    );
  }

  const monthIncome = dashboard?.monthIncome ?? 0;
  const totalIncome = dashboard?.totalIncome ?? 0;
  const taxRate = dashboard?.taxRate ?? 7;
  const monthIncomeAfterTax = dashboard?.monthIncomeAfterTax ?? monthIncome * (1 - taxRate / 100);
  const recentCompleted = dashboard?.recentCompleted || [];
  const byStatus = dashboard?.tasks?.byStatus || [];
  const totalTasks = byStatus.reduce((s, x) => s + parseInt(x.count, 10), 0);
  const completedCount = byStatus.find(s => s.status === 'completed')?.count ?? 0;
  const completionRate = totalTasks > 0 ? Math.round((parseInt(completedCount, 10) / totalTasks) * 100) : 0;

  const incomeByPeriod = financeChart.filter(f => f.type === 'income');
  const expenseByPeriod = financeChart.filter(f => f.type === 'expense');
  const allPeriods = [...new Set([...incomeByPeriod.map(i => i.period), ...expenseByPeriod.map(e => e.period)])].sort();
  const chartData = allPeriods.map(p => {
    const inc = incomeByPeriod.find(i => i.period === p)?.total ?? 0;
    const exp = expenseByPeriod.find(e => e.period === p)?.total ?? 0;
    return {
      period: p,
      доход: parseFloat(inc),
      расход: parseFloat(exp),
      прибыль: parseFloat(inc) - parseFloat(exp)
    };
  });

  const periodLabel = period === 'day' ? 'по дням' : period === 'week' ? 'по неделям' : 'по месяцам';

  return (
    <div className="analytics-page gnom-analytics analytics-new">
      <header className="analytics-new__header">
        <span className="analytics-new__header-left" />
        <h1 className="analytics-new__title">Аналитика</h1>
        <div className="analytics-new__tabs">
          {['day', 'week', 'month'].map((p) => (
            <button
              key={p}
              type="button"
              className={`analytics-new__tab ${period === p ? 'analytics-new__tab--active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p === 'day' ? 'День' : p === 'week' ? 'Неделя' : 'Месяц'}
            </button>
          ))}
        </div>
      </header>

      <div className="analytics-new__body">
        <p className="analytics-new__hint">Доход — только зарегистрированные оплаты. Личные задачи не учитываются.</p>

        <div className="analytics-new__cards">
          <div className="analytics-new__card analytics-new__card--income">
            <span className="analytics-new__card-label">Доход за месяц</span>
            <span className="analytics-new__card-value">{formatMoney(monthIncome)} ₽</span>
          </div>
          <div className="analytics-new__card analytics-new__card--total">
            <span className="analytics-new__card-label">Всего заработано</span>
            <span className="analytics-new__card-value">{formatMoney(totalIncome)} ₽</span>
          </div>
          <div className="analytics-new__card analytics-new__card--income">
            <span className="analytics-new__card-label">За месяц после налога ({taxRate}%)</span>
            <span className="analytics-new__card-value">{formatMoney(monthIncomeAfterTax)} ₽</span>
          </div>
          <div className="analytics-new__card analytics-new__card--tasks">
            <span className="analytics-new__card-label">Завершено задач</span>
            <span className="analytics-new__card-value">{completedCount} / {totalTasks}</span>
          </div>
          <div className="analytics-new__card analytics-new__card--rate">
            <span className="analytics-new__card-label">Выполнение</span>
            <span className="analytics-new__card-value">{completionRate}%</span>
            <div className="analytics-new__progress">
              <div className="analytics-new__progress-fill" style={{ width: `${completionRate}%` }} />
            </div>
          </div>
        </div>

        {chartData.length > 0 && (
          <section className="analytics-new__section">
            <h2 className="analytics-new__section-title">Финансы {periodLabel}</h2>
            <div className="analytics-new__chart-wrap">
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="anIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#81c784" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#81c784" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="anExpenseGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#e57373" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#e57373" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                  <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#1e2126', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10 }}
                    labelStyle={{ color: 'rgba(255,255,255,0.7)' }}
                    formatter={(value) => [formatMoney(value) + ' ₽', null]}
                  />
                  <Bar dataKey="доход" fill="url(#anIncomeGrad)" stroke="#81c784" radius={[4, 4, 0, 0]} name="Доход" />
                  <Bar dataKey="расход" fill="url(#anExpenseGrad)" stroke="#e57373" radius={[4, 4, 0, 0]} name="Расход" />
                  <Line type="monotone" dataKey="прибыль" stroke="#64b5f6" strokeWidth={2} dot={{ fill: '#64b5f6', r: 4 }} name="Прибыль" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {recentCompleted.length > 0 && (
          <section className="analytics-new__section">
            <h2 className="analytics-new__section-title">Недавно завершённые рабочие задачи</h2>
            <ul className="analytics-new__recent">
              {recentCompleted.slice(0, 5).map((task) => (
                <li key={task.id} className="analytics-new__recent-item">
                  <span className="analytics-new__recent-date">{formatTaskDate(task)}</span>
                  <span className="analytics-new__recent-title">{task.title || '—'}</span>
                  {task.client_name && <span className="analytics-new__recent-client">{task.client_name}</span>}
                  <span className="analytics-new__recent-sum">
                    {task.payment_status === 'paid'
                      ? `+${formatMoney(parseFloat(task.cost || 0))} ₽`
                      : 'не оплачено'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default Analytics;
